// Top-level .luis segment scanner for incremental parser reuse.
// It splits source into declaration and block segments with line metadata.
import type { LuisScannedSegment } from './types'
import { isSegmentWhitespaceOnly, isWhitespace, pushSegment } from './scanner-utils'

// Scan source into top-level segments while tracking quotes/comments/braces.
export function scanTopLevelLuisSegments(source: string): LuisScannedSegment[] | null {
  const segments: LuisScannedSegment[] = []
  let buffer = ''
  let depth = 0
  let line = 1
  let segmentLine = 1
  let quote: "'" | '"' | null = null
  let inBlockComment = false

  // Scan source and flush segments when top-level boundaries are reached.
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Keep block comment payload opaque.
    if (inBlockComment) {
      buffer += char
      if (char === '*' && next === '/') {
        buffer += '/'
        index += 1
        inBlockComment = false
      } else {
        // Continue scanning comment content.
      }
      if (char === '\n') {
        line += 1
      } else {
        // Keep line unchanged for non-newline chars.
      }
      continue
    } else {
      // Continue scanner outside block comments.
    }

    // Keep quoted content opaque to structure parsing.
    if (quote) {
      buffer += char
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Continue scanning quoted text.
      }
      if (char === '\n') {
        line += 1
      } else {
        // Keep line unchanged for non-newline chars.
      }
      continue
    } else {
      // Continue scanner outside quoted text.
    }

    // Enter block comment or quote mode.
    if (char === '/' && next === '*') {
      buffer += '/*'
      index += 1
      inBlockComment = true
      continue
    } else if (char === "'" || char === '"') {
      quote = char
      buffer += char
      continue
    } else {
      // Continue top-level structure parsing.
    }

    // Reset segment start line when first non-whitespace token appears.
    if (isSegmentWhitespaceOnly(buffer) && !isWhitespace(char)) {
      segmentLine = line
    } else {
      // Keep current segment start line.
    }

    // Flush declaration segment at top-level semicolon.
    if (char === ';' && depth === 0) {
      buffer += char
      pushSegment(segments, 'declaration', buffer, segmentLine)
      buffer = ''
      segmentLine = line
      continue
    } else {
      // Continue collecting declaration content.
    }

    // Update depth and flush completed top-level blocks.
    if (char === '{') {
      depth += 1
      buffer += char
      continue
    } else if (char === '}') {
      if (depth === 0) {
        // Return null so caller falls back to strict parser.
        return null
      } else {
        // Continue balanced block closing flow.
      }
      depth -= 1
      buffer += char
      if (depth === 0) {
        pushSegment(segments, 'block', buffer, segmentLine)
        buffer = ''
        segmentLine = line
      } else {
        // Keep collecting nested block content.
      }
      continue
    } else {
      // Continue scanning non-brace tokens.
    }

    buffer += char
    if (char === '\n') {
      line += 1
    } else {
      // Keep line unchanged for non-newline chars.
    }
  }

  // Reject incomplete source with unclosed structure.
  if (depth !== 0 || quote || inBlockComment) {
    // Return null so caller uses strict parser path.
    return null
  } else {
    // Continue trailing segment flush.
  }

  pushSegment(segments, 'declaration', buffer, segmentLine)
  // Return scanned top-level segment list.
  return segments
}
